import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { Icon, IconVariants } from "@validereinc/common-components";
import {
  SystemActionTypeType,
  UserTaskTypeType,
  WorkflowConfigStepType,
  WorkflowParallelTasksType,
  WorkflowStepType,
  WorkflowStepTypeType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import { produce } from "immer";
import { nanoid } from "nanoid";
import React from "react";
import {
  addStepToWorkflow,
  type BranchInfo,
} from "./WorkflowConfigurationAddNodeDrawer.helpers";
import styles from "./WorkflowConfigurationAddNodeDrawer.module.scss";

const cx = classNames.bind(styles);

export type NewNodeDetailsType = {
  label: string;
  icon: IconVariants;
  value: string;
  type: WorkflowStepTypeType;
  taskType: UserTaskTypeType | SystemActionTypeType | "parallel" | undefined;
  description?: string;
};

type WorkflowStepListItemProps = {
  stepDetails: NewNodeDetailsType;
  getNewStepConfigFromSelectedOption: (
    stepDetails: NewNodeDetailsType
  ) => Partial<WorkflowConfigStepType>;
  onClose: () => void;
};

export const WorkflowStepListItem = ({
  stepDetails,
  getNewStepConfigFromSelectedOption,
  onClose,
}: WorkflowStepListItemProps) => {
  const {
    currentConfiguration,
    setCurrentConfiguration,
    previousStepIdForNewStep = "",
    nextStepIdForNewStep,
    selectNode,
    markNodeAsDirty,
  } = useWorkflowGraphState();

  return (
    <div
      className={cx("workflowStepListItem")}
      onClick={() => {
        if (!currentConfiguration) return;

        onClose();

        const newStepId = `${stepDetails.taskType}-step-${nanoid(8)}`;
        const newStep = {
          ...getNewStepConfigFromSelectedOption(stepDetails),
        } as WorkflowConfigStepType;

        let branchInfo: BranchInfo = {
          willBeAddedToBranch: false,
          parallelStepId: undefined,
        };

        setCurrentConfiguration(
          produce(currentConfiguration, (draft) => {
            branchInfo = addStepToWorkflow(
              draft,
              newStep as Exclude<
                WorkflowConfigStepType,
                WorkflowParallelTasksType
              >,
              newStepId,
              previousStepIdForNewStep,
              nextStepIdForNewStep,
              markNodeAsDirty
            );
          })
        );

        if (newStep.type === WorkflowStepType.PARALLEL) {
          // Parallel steps don't have a drawer, just mark as dirty
          markNodeAsDirty(newStepId);
        } else if (
          branchInfo.willBeAddedToBranch &&
          branchInfo.parallelStepId
        ) {
          markNodeAsDirty(newStepId);
          markNodeAsDirty(branchInfo.parallelStepId);
        } else {
          selectNode(newStepId);
          markNodeAsDirty(newStepId);
        }
      }}
    >
      <div
        className={cx(
          "workflowStepListItemIconContainer",
          { userTask: stepDetails.type === WorkflowStepType.USER_TASK },
          {
            systemAction: stepDetails.type === WorkflowStepType.SYSTEM_ACTION,
          },
          { parallelStep: stepDetails.type === WorkflowStepType.PARALLEL }
        )}
      >
        <Icon
          variant={stepDetails.icon}
          size={22}
        />
      </div>

      {stepDetails.label}
    </div>
  );
};
